import { FirebaseError } from 'firebase/app';
import { Timestamp } from 'firebase/firestore';

import { TranslationType } from './utils';

export type InMailsType = 'SYSTEM';

/**
 * Tytuł InMaila może być stringiem (nowe logi wysyłek po uproszczeniu)
 * albo mapą tłumaczeń (stare dokumenty zapisane wcześniej).
 */
export type InMailTitle = string | TranslationType;

export const inMailTitleToString = (title: InMailTitle | undefined): string => {
  if (!title) return '';
  if (typeof title === 'string') return title;
  return title.pl ?? Object.values(title).find((v) => typeof v === 'string' && v) ?? '';
};

/** Tryb adresowania wysyłki. Backend rozwiązuje odbiorców z kolekcji `users`. */
export type InMailTarget =
    | { mode: 'users'; userIds: string[] }
    | { mode: 'all' }
    | { mode: 'organizations'; organizationIds: string[] };

/**
 * Żądanie wysyłki InMaila — jedna treść, bez tłumaczeń. Treść NIE jest
 * zapisywana; backend wysyła e-mail w szablonie Hotelspot i zapisuje log
 * wysyłki (liczniki + nieudane adresy).
 */
export interface InMailsSendRequest {
    title: string;
    content: string;
    image?: string;
    target: InMailTarget;
}

/** Status wysyłki do pojedynczego odbiorcy (zapisywany w logu). */
export interface InMailRecipientStatus {
    userId: string;
    email: string;
    status: 'sent' | 'failed';
    error?: string;
}

export type InMailsDataResponse = {
    data: InMailsFirestoreResponse[];
    totalPages: number;
}

/**
 * Dokument logu InMail czytany przez widok listy/detalu. Nowe logi mają
 * `title: string`, liczniki i `target`; pola starych dokumentów (description,
 * languages, …) są opcjonalne dla wstecznej zgodności widoku.
 */
export type InMailsFirestoreResponse = {
    id: string;
    title: InMailTitle;
    createdAt: Timestamp;
    image?: string;
    target?: InMailTarget;
    recipientCount?: number;
    sentCount?: number;
    failedCount?: number;
    failedRecipients?: InMailRecipientStatus[];
    // pola starych dokumentów (opcjonalne):
    type?: InMailsType;
    sendTo?: string[];
    description?: TranslationType;
    readBy?: string[];
    languages?: string[];
};

export type InMailsFirebaseResponse = InMailsFirestoreResponse;

export interface InMailsDataModel {
    data: InMailsFirestoreResponse[];
    totalPages: number;
    isLoading: boolean;
    error: FirebaseError | null;
}

/**
 * @deprecated Martwy typ po migracji do v3 — endpoint `updateDocumentInMail` i hook
 * `usePatchInMailFirestore` zostały usunięte razem z wielojęzycznym modelem InMaili.
 * Wysyłki idą przez `InMailsSendRequest` (v3-sendInMail). Do skasowania, gdy nic
 * już nie odwołuje się do starych dokumentów.
 */
export interface InMailsFirestoreDocumentRequest {
    title: TranslationType;
    description?: TranslationType;
    image?: string;
    sendTo?: string[];
    readBy?: string[];
    sendEmail: boolean;
    type: InMailsType | null;
    languages: string[];
}
